/**
 * OpenClaw integration.
 *
 * Spawns `openclaw agent --agent <id> --message <prompt> --json` as a child
 * process for each prompt. The gateway handles routing, model selection, and
 * session memory.
 *
 * Session isolation: uses `--session-id` to keep conversations separate per
 * ARBI user. The OpenClaw agent id (e.g. "arbi") is created once during
 * `arbi connect --agent openclaw` via `openclaw agents add`.
 *
 * The ARBI SKILL.md is installed as BOOTSTRAP.md in the agent's workspace
 * during setup, so the agent always has context about arbi commands.
 */

import { spawn } from 'node:child_process'
import type { Orchestrator } from '../orchestrator.js'

export interface OpenClawOrchestratorOptions {
  /** Path to the openclaw CLI binary (defaults to "openclaw") */
  binaryPath?: string
  /** ARBI session identifier — used for OpenClaw session isolation */
  sessionId: string
  /** OpenClaw agent id (defaults to "arbi") */
  agentId?: string
  /** Timeout in ms per prompt (defaults to 300_000 = 5 min) */
  timeoutMs?: number
  /**
   * Override the HOME env for the spawned openclaw subprocess. Set this in
   * multi-tenant deployments where ``arbi listen`` runs with a per-agent
   * HOME (for its own .arbi config) but openclaw should still resolve its
   * gateway config from the shared real ``/home/<user>`` — symlinks back
   * to the global ``.openclaw`` are rejected by openclaw's exec sandbox,
   * so a direct env override is the right escape hatch.
   */
  spawnHome?: string
}

export class OpenClawOrchestrator implements Orchestrator {
  readonly name = 'openclaw'

  private readonly binaryPath: string
  private readonly agentId: string
  private readonly sessionKey: string
  private readonly timeoutMs: number
  private readonly spawnHome?: string

  constructor(options: OpenClawOrchestratorOptions) {
    this.binaryPath = options.binaryPath ?? 'openclaw'
    this.agentId = options.agentId ?? 'arbi'
    // openclaw >= ~2026.4 rejects colons in session IDs. Use '-' as separator
    // (still distinct from any user-provided sessionId content).
    this.sessionKey = `arbi-${options.sessionId}`.replace(/[^a-zA-Z0-9_-]/g, '-')
    this.timeoutMs = options.timeoutMs ?? 300_000
    this.spawnHome = options.spawnHome
  }

  async prompt(prompt: string): Promise<string> {
    return this.spawnOpenClaw(prompt)
  }

  async close(): Promise<void> {
    // No persistent resources — each prompt spawns a fresh process
  }

  private spawnOpenClaw(message: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const args = [
        'agent',
        '--agent',
        this.agentId,
        '--session-id',
        this.sessionKey,
        '--message',
        message,
        '--json',
      ]

      const child = spawn(this.binaryPath, args, {
        stdio: ['ignore', 'pipe', 'pipe'],
        env: this.spawnHome ? { ...process.env, HOME: this.spawnHome } : process.env,
      })

      let stdout = ''
      let stderr = ''

      child.stdout.on('data', (chunk: Buffer) => {
        stdout += chunk.toString()
      })

      child.stderr.on('data', (chunk: Buffer) => {
        stderr += chunk.toString()
      })

      const timer = setTimeout(() => {
        child.kill('SIGTERM')
        reject(new Error(`OpenClaw prompt timed out after ${this.timeoutMs}ms`))
      }, this.timeoutMs)

      child.on('close', (code) => {
        clearTimeout(timer)
        if (code !== 0) {
          reject(
            new Error(`OpenClaw exited with code ${code}${stderr ? `: ${stderr.trim()}` : ''}`)
          )
          return
        }

        // --json output:
        //   openclaw 2026.5.x: { payloads: [{ text: "..." }], meta: {...} }
        //   older versions:   { result: { payloads: [{ text: "..." }] } }
        // Final belt-and-braces: meta.systemPromptReport.finalAssistantVisibleText.
        try {
          const json = JSON.parse(stdout)
          const payloads = json?.payloads ?? json?.result?.payloads ?? []
          const texts = payloads.map((p: { text?: string }) => p.text ?? '').filter(Boolean)
          if (texts.length > 0) {
            resolve(texts.join('\n'))
            return
          }
          const finalText =
            json?.meta?.systemPromptReport?.finalAssistantVisibleText ??
            json?.meta?.systemPromptReport?.finalAssistantRawText
          if (typeof finalText === 'string' && finalText.trim()) {
            resolve(finalText.trim())
            return
          }
          resolve(stdout.trim())
        } catch {
          resolve(stdout.trim())
        }
      })

      child.on('error', (err) => {
        clearTimeout(timer)
        reject(new Error(`Failed to spawn openclaw: ${err.message}`))
      })
    })
  }
}
